import mime from 'mime';

import {IMQUERY_POLICY} from '../constants';
import {Resize, ImageQueryResponseDto, ImageQueryFormData} from '../models';
import {getCdnLink} from './utils';

/**
 * Baut den Parameter String für eine ImageQuery
 */
export const buildImQueryString = (imageQuery: ImageQueryFormData): string => {
  const queryBuilder = new ImageQueryStringBuilder();
  if (imageQuery.resize) {
    queryBuilder.withResize(imageQuery.resize);
  }
  return queryBuilder.build();
};

/**
 * Prüft ob eine ImageQuery Manipulationen enthält
 *
 * @param imageQuery Die zu prüfende ImageQuery
 * @returns true wenn Manipulationen vorhanden sind
 */
export const hasImageManipulation = (
  imageQuery: ImageQueryResponseDto,
): boolean => {
  return imageQuery.imqueryString !== '';
};

/**
 * Erzeugt eine Akamai-CDN-URL für ein Bild und wendet die übergebene
 * Bildmanipulation an.
 *
 * @param downloadLink Pfad des Bildes (wie in `ResultItem.download`).
 * @param imageQuery Aktuelle Bildbearbeitungs Formdaten.
 * @param region Optionale Region (Sub-Domain) des CDN.
 */
export const buildManipulatedCdnLink = (
  downloadLink: string,
  imageQuery: ImageQueryResponseDto,
  region?: string,
): string => {
  const url = getCdnLink(downloadLink, region, IMQUERY_POLICY);
  const imageQueryString = imageQuery.imqueryString;
  const searchParams = new URLSearchParams(imageQueryString);

  searchParams.forEach((value, key) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

/**
 * Lädt ein manipuliertes Bild direkt vom CDN herunter.
 *
 * Diese Funktion wird verwendet wenn ImageQuery-Manipulationen angewendet wurden,
 * da das Backend diese nicht verarbeiten kann (lädt nur vom lokalen Volume).
 *
 * Das Bild wird als Blob geladen und dann per Blob-URL heruntergeladen.
 * Das <a>-Element wird nicht dem DOM hinzugefügt, was CSP-konform ist.
 *
 * @param downloadLink Pfad des Bildes (wie in `ResultItem.download`).
 * @param imageQuery Aktuelle Bildbearbeitungs-Einstellungen.
 * @param region Optionale Region (Sub-Domain) des CDN.
 * @param fileName Optionaler Dateiname für den Download.
 */
export const downloadManipulatedImageFromCdn = async (
  downloadLink: string,
  imageQuery: ImageQueryResponseDto,
  region?: string,
  fileName?: string,
): Promise<void> => {
  const cdnUrl = buildManipulatedCdnLink(downloadLink, imageQuery, region);

  // Lade Bild als Blob vom CDN
  const response = await fetch(cdnUrl);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const blob = await response.blob();

  const downloadFileName = ensureFileExtension(
    fileName || 'bild',
    blob.type,
    response.headers.get('content-type') || undefined,
  );

  const blobUrl = URL.createObjectURL(blob);

  // Erstelle temporäres <a>-Element
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = downloadFileName;

  // Trigger Download per programmatischem Click
  a.click();

  // Cleanup nach kurzer Verzögerung
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 100);
};

/**
 * Stellt sicher, dass ein Dateiname eine passende Dateiendung hat.
 * Falls die vorhandene Extension nicht zum MIME-Type passt, wird sie ersetzt.
 *
 * Dies ist wichtig, da CDN-Links oft den Original-Dateinamen enthalten,
 * das Bild aber in einem anderen Format (z.B. WebP) ausgeliefert wird.
 *
 * @param fileName Der ursprüngliche Dateiname (kann bereits eine Extension haben)
 * @param blobType Der MIME-Type des Blobs
 * @param contentType Optional der Content-Type aus dem Response-Header
 * @returns Dateiname mit passender Extension für den tatsächlichen MIME-Type
 */
function ensureFileExtension(
  fileName: string,
  blobType: string,
  contentType?: string,
): string {
  const mimeType = blobType || contentType;
  if (!mimeType) {
    return fileName;
  }

  const correctExtension = mime.getExtension(mimeType);
  if (!correctExtension) {
    return fileName;
  }

  const nameWithoutExt = fileName.replace(/\.[^.]*$/, '');
  return `${nameWithoutExt}.${correctExtension}`;
}

class ImageQueryStringBuilder {
  private imQueryString: string | undefined;

  withResize(resize: Resize) {
    let imQueryString = this.imQueryString
      ? `${this.imQueryString},Resize`
      : `Resize`;
    if (resize.width) {
      imQueryString = `${imQueryString},width=${resize.width}`;
    }
    if (resize.height) {
      imQueryString = `${imQueryString},height=${resize.height}`;
    }
    imQueryString = `${imQueryString},aspect=${resize.aspect}`;
    this.imQueryString = imQueryString;
    return this;
  }

  build(): string {
    return this.imQueryString ? `im=${this.imQueryString}` : '';
  }
}
